import React, { useMemo } from "react";
import styled, { useTheme } from "styled-components";
import { useSelector } from "react-redux";
import { RawChartData, SingleValueChartData } from "@shared/types";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { round } from "@shared/utils/numbers";
import useGraphDataSocket from "@shared/services/socket/useGraphDataSocket";
import { DISPLAY_CURRENCIES, TIME_RANGE_1_DAY } from "common/constants";
import {
  directionColor,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";
import NewLookSparkline from "features/portfolio/NewLook/NewLookSparkline.tsx";
import { formatSignedPercent } from "features/portfolio/NewLook/format.ts";

type GraphData = RawChartData | SingleValueChartData;

type TrendLayout = "inline" | "stacked";

interface NewLookTrendCellProps {
  data?: GraphData;
  /** Series column: 1 for USD, 2 for BTC; defaults to the display currency. */
  yValueIndex?: number;
  /** Stacked puts the change under the line (phone rows, cards). */
  layout?: TrendLayout;
  /** Sparkline size; defaults fit a table cell. */
  sparkWidth?: number;
  sparkHeight?: number;
  /** Adds the soft area fill under the line (cards). */
  fill?: boolean;
}

/**
 * A table cell's 24h trend: a small smooth line and the change beside it,
 * both in the direction's colour.
 */
export const NewLookTrendCell = ({
  data,
  yValueIndex,
  layout = "inline",
  sparkWidth,
  sparkHeight = 24,
  fill,
}: NewLookTrendCellProps) => {
  const tokens = getNewLookTokens(useTheme());
  const displayCurrency = useSelector(selectDisplayCurrency);
  const index =
    yValueIndex || (displayCurrency === DISPLAY_CURRENCIES.BTC ? 2 : 1);

  const { values, change } = useMemo(() => {
    const points = data?.[TIME_RANGE_1_DAY] || [];
    const rawChange = Array.isArray(data?.change)
      ? data?.change[index - 1]
      : data?.change;
    return {
      values: points.map((point) => Number(point[index])),
      change: points.length > 1 ? round((rawChange || 0) * 100, 2) : undefined,
    };
  }, [data, index]);

  if (change === undefined) return null;
  const color = change ? directionColor(tokens, change) : tokens.muted;
  return (
    <Cell $layout={layout}>
      <NewLookSparkline
        values={values}
        color={color}
        width={sparkWidth ?? (layout === "stacked" ? 72 : 64)}
        height={sparkHeight}
        lineOnly={!fill}
      />
      <Change style={{ color }}>{formatSignedPercent(change)}</Change>
    </Cell>
  );
};

interface NewLookLiveTrendCellProps
  extends Omit<NewLookTrendCellProps, "data"> {
  symbol: string;
  exchange: string;
  initialGraphData: GraphData;
}

/** The trend cell, kept live by the price socket. */
export const NewLookLiveTrendCell = ({
  symbol,
  exchange,
  initialGraphData,
  ...props
}: NewLookLiveTrendCellProps) => {
  const { graphData } = useGraphDataSocket(symbol, exchange);
  return <NewLookTrendCell data={graphData ?? initialGraphData} {...props} />;
};

const Cell = styled.div<{ $layout: TrendLayout }>`
  display: flex;
  align-items: ${(p) => (p.$layout === "stacked" ? "flex-end" : "center")};
  flex-direction: ${(p) => (p.$layout === "stacked" ? "column" : "row")};
  justify-content: flex-end;
  gap: ${(p) => (p.$layout === "stacked" ? 4 : 12)}px;
`;
const Change = styled.span`
  min-width: 56px;
  ${newLookType.meta};
  font-size: 13px;
  font-weight: 500;
  text-align: right;
`;
